// The phone layout (AppShell switches to it below 768px): full-screen pages with a floating tab bar,
// bottom sheets instead of menus, and tasks as a full page instead of a modal.
import '@/styles/mobile.css';
import { lazy, Suspense, useEffect, useRef, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from '@/components/ui/icons';
import { useUi } from '@/lib/ui';
import { useWorkspaceId } from '@/lib/nav';
import { DialogHost } from '@/shell/dialogs';
import { CommandPalette } from '@/command/CommandPalette';
import { CreateTaskModal } from '@/task/CreateTaskModal';
import { FieldEditorHost } from '@/views/FieldsPanel';
import { NotificationPrompt } from '@/shell/NotificationPrompt';
import { TabBar } from './TabBar';
import { MobileSheets, launchPage } from './MobileSheets';
import { MobileHome } from './MobileHome';
import { MobileInbox } from './MobileInbox';
import { MobileMyTasks } from './MobileMyTasks';
import { MobileSpaces, MobileTree } from './MobileSpaces';
import { MobileLocation } from './MobileLocation';
import { MobileTask } from './MobileTask';
import { MobileChat } from './MobileChat';

const RepliesPage = lazy(() => import('@/pages/Inbox').then((m) => ({ default: m.RepliesPage })));
const AssignedCommentsPage = lazy(() => import('@/pages/Inbox').then((m) => ({ default: m.AssignedCommentsPage })));
const ChatPage = lazy(() => import('@/pages/Chat').then((m) => ({ default: m.ChatPage })));
const DocsPage = lazy(() => import('@/pages/Docs').then((m) => ({ default: m.DocsPage })));
const PlannerPage = lazy(() => import('@/pages/Planner').then((m) => ({ default: m.PlannerPage })));
const TeamsPage = lazy(() => import('@/pages/Teams').then((m) => ({ default: m.TeamsPage })));
const TimesheetsPage = lazy(() => import('@/pages/Timesheets').then((m) => ({ default: m.TimesheetsPage })));
const SettingsPage = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.SettingsPage })));

/** A page title bar with a back button, for screens that aren't one of the tabs. */
export function MobileHeader({ title, sub, icon, actions, back }: { title: ReactNode; sub?: ReactNode; icon?: ReactNode; actions?: ReactNode; back?: string }) {
  const navigate = useNavigate();
  const ws = useWorkspaceId();
  return (
    <header className="m-header">
      <button type="button" className="m-icon-btn" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(back ?? `/${ws}/home`))} aria-label="Back">
        <ChevronLeft size={22} />
      </button>
      {icon && <span className="m-header-icon">{icon}</span>}
      <div className="m-header-text">
        <div className="m-header-title truncate">{title}</div>
        {sub && <div className="m-header-sub truncate">{sub}</div>}
      </div>
      {actions && <div className="m-header-actions">{actions}</div>}
    </header>
  );
}

/** An existing desktop page shown full-screen under a mobile header. */
function Framed({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="m-framed">
      <MobileHeader title={title} />
      <div className="m-framed-body">{children}</div>
    </div>
  );
}

function TreeRoute({ type }: { type: 'space' | 'folder' }) {
  const { id = '', viewId } = useParams();
  // a view of the space or folder shows its tasks; the plain address shows what's inside
  return viewId ? <MobileLocation key={`${type}:${id}:${viewId}`} location={{ type, id }} viewId={viewId} /> : <MobileTree key={`${type}:${id}`} type={type} id={id} />;
}

function ListRoute({ type }: { type: 'list' | 'workspace' }) {
  const { ws = '', id, viewId } = useParams();
  return <MobileLocation key={`${type}:${id ?? ws}:${viewId ?? ''}`} location={{ type, id: type === 'workspace' ? ws : (id ?? '') }} viewId={viewId} />;
}

// pages that keep the tab bar; others (a list, a conversation, a doc, settings) have their own bottom
const WITH_TABS = new Set(['home', 'inbox', 'my-tasks', 'replies', 'assigned-comments', 'spaces', 'planner', 'teams', 'timesheets']);

export function MobileShell() {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const taskId = params.get('t');
  const commandOpen = useUi((s) => s.commandOpen);
  const createOpen = useUi((s) => s.createTaskOpen);
  const parts = pathname.split('/');
  const seg = parts[2] ?? '';
  // conversations keep the tab bar too (their composer floats above it), like Slack
  const showTabs = WITH_TABS.has(seg) || seg === 'chat' || (seg === 's' && !parts[4]) || (seg === 'f' && !parts[4]) || (seg === 'docs' && !parts[3]);

  // open on the launch page chosen in Settings, once per app start
  const launched = useRef(false);
  useEffect(() => {
    if (launched.current) return;
    launched.current = true;
    const page = launchPage();
    if (seg === 'home' && page !== 'home') navigate(`/${ws}/${page}`, { replace: true });
  }, [seg, ws, navigate]);

  return (
    <div className="m-app">
      <main className={showTabs ? 'm-main with-tabs' : 'm-main'}>
        <NotificationPrompt />
        <Suspense fallback={<div className="page-placeholder" />}>
          <Routes>
            <Route path="home" element={<MobileHome />} />
            <Route path="inbox" element={<MobileInbox />} />
            <Route path="my-tasks" element={<MobileMyTasks />} />
            <Route path="spaces" element={<MobileSpaces />} />
            <Route path="replies" element={<Framed title="Replies"><RepliesPage /></Framed>} />
            <Route path="assigned-comments" element={<Framed title="Assigned comments"><AssignedCommentsPage /></Framed>} />
            <Route path="l/:id/:viewId?" element={<ListRoute type="list" />} />
            <Route path="f/:id/:viewId?" element={<TreeRoute type="folder" />} />
            <Route path="s/:id/:viewId?" element={<TreeRoute type="space" />} />
            <Route path="everything/:viewId?" element={<ListRoute type="workspace" />} />
            <Route path="chat/activity" element={<ChatPage view="activity" />} />
            <Route path="chat/:channelId?" element={<ChatPage home={<MobileChat />} />} />
            <Route path="docs/:docId?/:pageId?" element={<DocsPage />} />
            <Route path="planner" element={<PlannerPage />} />
            <Route path="teams" element={<TeamsPage />} />
            <Route path="timesheets" element={<TimesheetsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="home" replace />} />
          </Routes>
        </Suspense>
      </main>
      {showTabs && <TabBar />}
      {taskId && <MobileTask key={taskId} taskId={taskId} />}
      {commandOpen && <CommandPalette />}
      {createOpen && <CreateTaskModal />}
      <MobileSheets />
      <DialogHost />
      <FieldEditorHost />
    </div>
  );
}
